import { inject, Injectable } from '@angular/core';

import { type VariableInfo } from '@blaze-shared/api-contracts-domain';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import {
  isDateOrDateTimeString,
  maxDateString,
  minDateString,
  toEpoch,
} from '@blaze-shared/shared-util';
import { normalizeVarName } from '@blaze-shared/variables-util';
import { type StoreVariableWorkflowNode } from '@blaze-shared/workflow-domain';

/**
 * Works out the value a `StoreVariable` node saves.
 *
 * The node's expression decides how: an aggregate function over a list of rows reduces them to one
 * value, anything else is evaluated as a plain expression. An aggregate over no rows falls back to
 * evaluating the function's own argument, which is how an author supplies a default —
 * `MAX($$input1.OrderId || @@lastOrderId)`.
 */
@Injectable({ providedIn: 'root' })
export class StoreVariableActionService {
  private readonly iss = inject(IntelliSenseService);

  storevariable(
    node: StoreVariableWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
    projectId: string,
  ): VariableInfo {
    const storedVar: VariableInfo = {
      id: '',
      name: normalizeVarName(node.name || node.outputName || ''),
      projectId,
      scope: node.scope,
      value: '',
    };
    if (node.scope === 'Account') {
      storedVar.scopeValue = ctx?.['$$Account']?.['Name'] ?? '';
    }

    const fnName = this.iss.getAggregateFunctionName(node.value)?.toLowerCase();
    if (!fnName || !Array.isArray(nodeInput)) {
      // A one-row list holds a single value as far as a plain expression is concerned.
      const input =
        !fnName && Array.isArray(nodeInput) && nodeInput.length === 1 ? nodeInput[0] : nodeInput;
      storedVar.value = this.iss.eval(node.value, this.iss.updateRuntimeContext(ctx, input)) ?? '';
      return storedVar;
    }

    const fn = this.iss.createAggregateFunction(node.value);
    storedVar.value = this.aggregate(fnName, nodeInput, fn.column, ctx) ?? 0;
    return storedVar;
  }

  private aggregate(
    fnName: string,
    rows: any[],
    column: string,
    ctx: RuntimeContext,
  ): string | number | undefined {
    if (fnName === 'count') {
      return rows.length;
    }
    if (!rows.length) {
      return this.iss.eval(column, this.iss.updateRuntimeContext(ctx, {}));
    }
    switch (fnName) {
      case 'avg':
        return this.getAverageFromEval(rows, column, ctx);
      case 'max':
        return this.getMaxFromEval(rows, column, ctx);
      case 'min':
        return this.getMinFromEval(rows, column, ctx);
      case 'sum':
        return this.getSumFromEval(rows, column, ctx);
      default:
        return undefined;
    }
  }

  private getAverageFromEval(rows: any[], column: string, ctx: RuntimeContext): number {
    return this.getSumFromEval(rows, column, ctx) / rows?.length;
  }

  /** Dates are compared as timestamps and returned as the string that produced the maximum. */
  private getMaxFromEval(rows: any[], column: string, ctx: RuntimeContext): string | number {
    let max = Number.MIN_SAFE_INTEGER;
    let maxDate = '';
    rows.forEach((row) => {
      const colValue = this.iss.eval(column, this.iss.updateRuntimeContext(ctx, row));
      if (isDateOrDateTimeString(colValue)) {
        maxDate = maxDateString(colValue, max) ?? maxDate;
        max = toEpoch(maxDate);
      } else {
        max = Math.max(max, colValue);
      }
    });
    return maxDate || max;
  }

  private getMinFromEval(rows: any[], column: string, ctx: RuntimeContext): string | number {
    let min = Number.MAX_SAFE_INTEGER;
    let minDate = '';
    rows.forEach((row) => {
      const colValue = this.iss.eval(column, this.iss.updateRuntimeContext(ctx, row));
      if (isDateOrDateTimeString(colValue)) {
        minDate = minDateString(colValue, min) ?? minDate;
        min = toEpoch(minDate);
      } else {
        min = Math.min(min, colValue);
      }
    });
    return minDate || min;
  }

  private getSumFromEval(rows: any[], column: string, ctx: RuntimeContext): number {
    return rows.reduce(
      (sum, row) => sum + this.iss.eval(column, this.iss.updateRuntimeContext(ctx, row)),
      0,
    );
  }
}
